import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useAnimation, useMotionValue, useTransform } from "framer-motion";

// ═══════════════════════════════════════════════════════════════════════════════
// ADVANCED CANVAS-BASED PARTICLE SYSTEM WITH NEURAL NETWORK EFFECT
// ═══════════════════════════════════════════════════════════════════════════════

const CanvasParticles = ({ isOrbHovered }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const centerRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;

    // Store center position for clustering effect
    centerRef.current = { x: width * 0.65, y: height * 0.5 };

    // High DPI support
    const dpr = Math.min(window.devicePixelRatio, 2);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Particle class
    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.originalX = this.x;
        this.originalY = this.y;
        this.size = Math.random() * 3.5 + 1;
        this.baseSpeedX = (Math.random() - 0.5) * 0.6;
        this.baseSpeedY = (Math.random() - 0.5) * 0.6;
        this.speedX = this.baseSpeedX;
        this.speedY = this.baseSpeedY;
        this.life = Math.random() * 150 + 100;
        this.maxLife = this.life;
        this.hue = Math.random() * 60 + 220;
        this.saturation = 70 + Math.random() * 30;
        this.lightness = 50 + Math.random() * 30;
        this.pulseOffset = Math.random() * Math.PI * 2;
      }

      update(time, isHovered) {
        // Mouse interaction - repel from cursor
        const dx = mouseRef.current.x - this.x;
        const dy = mouseRef.current.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < 180) {
          const force = (180 - dist) / 180;
          this.speedX -= (dx / dist) * force * 0.025;
          this.speedY -= (dy / dist) * force * 0.025;
        }

        // Clustering effect when orb is hovered - attract to center
        if (isHovered) {
          const cdx = centerRef.current.x - this.x;
          const cdy = centerRef.current.y - this.y;
          const cdist = Math.sqrt(cdx * cdx + cdy * cdy);
          
          if (cdist > 50) {
            const attractForce = 0.015;
            this.speedX += (cdx / cdist) * attractForce;
            this.speedY += (cdy / cdist) * attractForce;
          }
          
          // Increase brightness when hovering
          this.lightness = Math.min(80, this.lightness + 0.5);
        } else {
          // Return to normal
          this.lightness = Math.max(50 + Math.random() * 30, this.lightness - 0.3);
        }

        // Pulsing size
        this.currentSize = this.size * (1 + Math.sin(time * 2 + this.pulseOffset) * 0.3);

        this.x += this.speedX;
        this.y += this.speedY;
        this.life--;

        // Apply friction
        this.speedX *= 0.99;
        this.speedY *= 0.99;

        // Add back base speed
        this.speedX += this.baseSpeedX * 0.01;
        this.speedY += this.baseSpeedY * 0.01;

        // Boundary check with wrap
        if (this.x < -50) this.x = width + 50;
        if (this.x > width + 50) this.x = -50;
        if (this.y < -50) this.y = height + 50;
        if (this.y > height + 50) this.y = -50;

        if (this.life <= 0) this.reset();
      }

      draw(ctx, isHovered) {
        const opacity = this.life / this.maxLife;
        const glowMultiplier = isHovered ? 1.5 : 1;
        
        // Outer glow
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.currentSize * 4 * glowMultiplier, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, ${opacity * 0.1 * glowMultiplier})`;
        ctx.fill();

        // Inner glow
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.currentSize * 2 * glowMultiplier, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, ${opacity * 0.2 * glowMultiplier})`;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.currentSize, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${this.hue}, ${this.saturation}%, ${this.lightness}%, ${opacity * 0.9})`;
        ctx.fill();
      }
    }

    // Initialize particles
    const particleCount = 180;
    particlesRef.current = [];
    for (let i = 0; i < particleCount; i++) {
      particlesRef.current.push(new Particle());
    }

    // Draw connections with gradient
    const drawConnections = (isHovered) => {
      const connectionDistance = isHovered ? 150 : 120;
      const baseOpacity = isHovered ? 0.3 : 0.2;
      
      for (let i = 0; i < particlesRef.current.length; i++) {
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const p1 = particlesRef.current[i];
          const p2 = particlesRef.current[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < connectionDistance) {
            const opacity = (1 - dist / connectionDistance) * baseOpacity;
            const gradient = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
            gradient.addColorStop(0, `hsla(${p1.hue}, ${p1.saturation}%, ${p1.lightness}%, ${opacity})`);
            gradient.addColorStop(1, `hsla(${p2.hue}, ${p2.saturation}%, ${p2.lightness}%, ${opacity})`);
            
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = gradient;
            ctx.lineWidth = isHovered ? 1.2 : 0.8;
            ctx.stroke();
          }
        }
      }
    };

    let time = 0;
    let currentHovered = false;
    
    // Animation loop
    const animate = () => {
      time += 0.016;
      currentHovered = isOrbHovered;
      ctx.clearRect(0, 0, width, height);
      
      particlesRef.current.forEach((particle) => {
        particle.update(time, currentHovered);
        particle.draw(ctx, currentHovered);
      });

      drawConnections(currentHovered);
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Mouse tracking
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
    };

    // Resize handler
    const handleResize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      centerRef.current = { x: width * 0.65, y: height * 0.5 };
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    };

    canvas.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationRef.current);
      canvas.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOrbHovered]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ pointerEvents: "auto" }}
    />
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// INTERACTIVE ADVANCED ANIMATED ORB
// ═══════════════════════════════════════════════════════════════════════════════

const AdvancedOrb = ({ onHoverChange }) => {
  const [isHovered, setIsHovered] = useState(false);
  const controls = useAnimation();

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    onHoverChange?.(true);
    controls.start("hover");
  }, [onHoverChange, controls]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onHoverChange?.(false);
    controls.start("normal");
  }, [onHoverChange, controls]);

  // Ring animation variants
  const ring1Variants = {
    normal: { 
      rotateZ: [0, 360],
      transition: { rotateZ: { duration: 8, repeat: Infinity, ease: "linear" } }
    },
    hover: { 
      rotateZ: [0, 360],
      transition: { rotateZ: { duration: 3, repeat: Infinity, ease: "linear" } }
    }
  };

  const ring2Variants = {
    normal: { 
      rotateZ: [360, 0],
      transition: { rotateZ: { duration: 12, repeat: Infinity, ease: "linear" } }
    },
    hover: { 
      rotateZ: [360, 0],
      transition: { rotateZ: { duration: 4, repeat: Infinity, ease: "linear" } }
    }
  };

  const ring3Variants = {
    normal: { 
      rotateZ: [0, 360],
      transition: { rotateZ: { duration: 20, repeat: Infinity, ease: "linear" } }
    },
    hover: { 
      rotateZ: [0, 360],
      transition: { rotateZ: { duration: 6, repeat: Infinity, ease: "linear" } }
    }
  };

  return (
    <div 
      className="relative w-full h-full flex items-center justify-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Hover detection area */}
      <div 
        className="absolute z-10 rounded-full cursor-pointer"
        style={{ width: "500px", height: "500px" }}
      />

      {/* Main glowing orb with gradient */}
      <motion.div
        className="absolute"
        animate={{
          scale: isHovered ? [1.08, 1.15, 1.08] : [1, 1.05, 1],
          rotate: [0, 180, 360],
        }}
        transition={{
          scale: { duration: isHovered ? 1.5 : 4, repeat: Infinity, ease: "easeInOut" },
          rotate: { duration: isHovered ? 10 : 20, repeat: Infinity, ease: "linear" },
        }}
        style={{
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: isHovered 
            ? `radial-gradient(circle at 30% 30%, 
                rgba(167, 139, 250, 0.6) 0%, 
                rgba(123, 104, 238, 0.35) 30%, 
                rgba(96, 165, 250, 0.2) 60%, 
                transparent 70%)`
            : `radial-gradient(circle at 30% 30%, 
                rgba(167, 139, 250, 0.4) 0%, 
                rgba(123, 104, 238, 0.2) 30%, 
                rgba(96, 165, 250, 0.1) 60%, 
                transparent 70%)`,
          boxShadow: isHovered
            ? `0 0 150px rgba(123, 104, 238, 0.5),
               0 0 300px rgba(123, 104, 238, 0.3),
               inset 0 0 150px rgba(167, 139, 250, 0.2)`
            : `0 0 100px rgba(123, 104, 238, 0.3),
               0 0 200px rgba(123, 104, 238, 0.2),
               inset 0 0 100px rgba(167, 139, 250, 0.1)`,
          transition: "background 0.5s ease, box-shadow 0.5s ease",
        }}
      />

      {/* Rotating ring 1 - fast */}
      <motion.div
        initial={{ rotateX: 75, rotateZ: 0 }}
        animate={isHovered ? "hover" : "normal"}
        variants={ring1Variants}
        style={{
          position: "absolute",
          width: isHovered ? "500px" : "480px",
          height: isHovered ? "500px" : "480px",
          borderRadius: "50%",
          border: isHovered ? "3px solid transparent" : "2px solid transparent",
          borderTopColor: isHovered ? "rgba(123, 104, 238, 0.8)" : "rgba(123, 104, 238, 0.5)",
          borderRightColor: isHovered ? "rgba(123, 104, 238, 0.5)" : "rgba(123, 104, 238, 0.3)",
          rotateX: 75,
          transition: "width 0.5s ease, height 0.5s ease, border 0.5s ease",
        }}
      />

      {/* Rotating ring 2 - medium */}
      <motion.div
        initial={{ rotateX: 60, rotateZ: 360 }}
        animate={isHovered ? "hover" : "normal"}
        variants={ring2Variants}
        style={{
          position: "absolute",
          width: isHovered ? "550px" : "520px",
          height: isHovered ? "550px" : "520px",
          borderRadius: "50%",
          border: isHovered ? "2px solid transparent" : "1px solid transparent",
          borderTopColor: isHovered ? "rgba(96, 165, 250, 0.7)" : "rgba(96, 165, 250, 0.4)",
          borderLeftColor: isHovered ? "rgba(96, 165, 250, 0.4)" : "rgba(96, 165, 250, 0.2)",
          rotateX: 60,
          transition: "width 0.5s ease, height 0.5s ease, border 0.5s ease",
        }}
      />

      {/* Rotating ring 3 - slow */}
      <motion.div
        initial={{ rotateX: 45, rotateY: 30, rotateZ: 0 }}
        animate={isHovered ? "hover" : "normal"}
        variants={ring3Variants}
        style={{
          position: "absolute",
          width: isHovered ? "600px" : "560px",
          height: isHovered ? "600px" : "560px",
          borderRadius: "50%",
          border: isHovered ? "2px solid transparent" : "1px solid transparent",
          borderTopColor: isHovered ? "rgba(167, 139, 250, 0.6)" : "rgba(167, 139, 250, 0.3)",
          borderBottomColor: isHovered ? "rgba(167, 139, 250, 0.3)" : "rgba(167, 139, 250, 0.15)",
          rotateX: 45,
          rotateY: 30,
          transition: "width 0.5s ease, height 0.5s ease, border 0.5s ease",
        }}
      />

      {/* Fourth ring - appears on hover */}
      <motion.div
        animate={{
          rotateX: 30,
          rotateY: -45,
          rotateZ: isHovered ? [0, 360] : [0, 360],
          opacity: isHovered ? 1 : 0,
          scale: isHovered ? 1 : 0.8,
        }}
        transition={{
          rotateZ: { duration: 5, repeat: Infinity, ease: "linear" },
          opacity: { duration: 0.3 },
          scale: { duration: 0.3 },
        }}
        style={{
          position: "absolute",
          width: "450px",
          height: "450px",
          borderRadius: "50%",
          border: "2px solid transparent",
          borderTopColor: "rgba(192, 132, 252, 0.6)",
          borderRightColor: "rgba(192, 132, 252, 0.3)",
        }}
      />

      {/* Inner geometric shape */}
      <motion.div
        animate={{ 
          rotate: [0, 360],
          scale: isHovered ? 1.2 : 1,
        }}
        transition={{ 
          rotate: { duration: isHovered ? 8 : 15, repeat: Infinity, ease: "linear" },
          scale: { duration: 0.3 },
        }}
        style={{
          position: "absolute",
          width: "200px",
          height: "200px",
          border: isHovered ? "2px solid rgba(123, 104, 238, 0.5)" : "1px solid rgba(123, 104, 238, 0.3)",
          transition: "border 0.3s ease",
        }}
      />

      {/* Inner octagon */}
      <motion.div
        animate={{ 
          rotate: [360, 0],
          scale: isHovered ? 1.3 : 1,
        }}
        transition={{ 
          rotate: { duration: isHovered ? 10 : 20, repeat: Infinity, ease: "linear" },
          scale: { duration: 0.3 },
        }}
        style={{
          position: "absolute",
          width: "150px",
          height: "150px",
          background: "transparent",
          border: isHovered ? "2px solid rgba(167, 139, 250, 0.45)" : "1px solid rgba(167, 139, 250, 0.25)",
          clipPath: "polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)",
          transition: "border 0.3s ease",
        }}
      />

      {/* Inner hexagon - appears on hover */}
      <motion.div
        animate={{ 
          rotate: [0, 360],
          opacity: isHovered ? 1 : 0,
          scale: isHovered ? 1 : 0.5,
        }}
        transition={{ 
          rotate: { duration: 12, repeat: Infinity, ease: "linear" },
          opacity: { duration: 0.3 },
          scale: { duration: 0.3 },
        }}
        style={{
          position: "absolute",
          width: "100px",
          height: "100px",
          background: "transparent",
          border: "1px solid rgba(96, 165, 250, 0.4)",
          clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
        }}
      />

      {/* Core pulsing center */}
      <motion.div
        animate={{
          scale: isHovered ? [1.5, 2, 1.5] : [1, 1.5, 1],
          opacity: isHovered ? [0.9, 1, 0.9] : [0.8, 1, 0.8],
        }}
        transition={{ duration: isHovered ? 1 : 2, repeat: Infinity, ease: "easeInOut" }}
        style={{
          width: isHovered ? "30px" : "20px",
          height: isHovered ? "30px" : "20px",
          borderRadius: "50%",
          background: isHovered 
            ? "linear-gradient(135deg, #C4B5FD, #8B5CF6)"
            : "linear-gradient(135deg, #A78BFA, #7B68EE)",
          boxShadow: isHovered
            ? `0 0 50px rgba(139, 92, 246, 1),
               0 0 100px rgba(123, 104, 238, 0.8),
               0 0 150px rgba(167, 139, 250, 0.6)`
            : `0 0 30px rgba(123, 104, 238, 0.8),
               0 0 60px rgba(123, 104, 238, 0.6),
               0 0 90px rgba(167, 139, 250, 0.4)`,
          transition: "width 0.3s ease, height 0.3s ease, background 0.3s ease, box-shadow 0.3s ease",
        }}
      />

      {/* Orbiting dots */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <motion.div
          key={i}
          animate={{
            rotate: [i * 60, i * 60 + 360],
          }}
          transition={{ 
            duration: isHovered ? (5 + i) : (10 + i * 2), 
            repeat: Infinity, 
            ease: "linear" 
          }}
          style={{
            position: "absolute",
            width: isHovered ? "400px" : "350px",
            height: isHovered ? "400px" : "350px",
            transition: "width 0.5s ease, height 0.5s ease",
          }}
        >
          <motion.div
            animate={{
              opacity: isHovered ? [0.6, 1, 0.6] : [0.3, 0.8, 0.3],
              scale: isHovered ? [1.2, 1.5, 1.2] : [1, 1.2, 1],
            }}
            transition={{ duration: isHovered ? 1 : 2 + i * 0.5, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute",
              top: "0",
              left: "50%",
              width: isHovered ? `${12 - i}px` : `${8 - i}px`,
              height: isHovered ? `${12 - i}px` : `${8 - i}px`,
              borderRadius: "50%",
              background: ["#7B68EE", "#60A5FA", "#A78BFA", "#818CF8", "#C4B5FD", "#60A5FA"][i],
              boxShadow: isHovered 
                ? `0 0 20px ${["#7B68EE", "#60A5FA", "#A78BFA", "#818CF8", "#C4B5FD", "#60A5FA"][i]}`
                : `0 0 10px ${["#7B68EE", "#60A5FA", "#A78BFA", "#818CF8", "#C4B5FD", "#60A5FA"][i]}`,
              transform: "translateX(-50%)",
              transition: "width 0.3s ease, height 0.3s ease, box-shadow 0.3s ease",
            }}
          />
        </motion.div>
      ))}

      {/* Additional orbiting dots on hover */}
      {[0, 1, 2, 3].map((i) => (
        <motion.div
          key={`extra-${i}`}
          animate={{
            rotate: [i * 90 + 45, i * 90 + 405],
            opacity: isHovered ? 1 : 0,
          }}
          transition={{ 
            rotate: { duration: 4 + i, repeat: Infinity, ease: "linear" },
            opacity: { duration: 0.3 },
          }}
          style={{
            position: "absolute",
            width: "320px",
            height: "320px",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "0",
              left: "50%",
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: ["#E879F9", "#34D399", "#FBBF24", "#F472B6"][i],
              boxShadow: `0 0 15px ${["#E879F9", "#34D399", "#FBBF24", "#F472B6"][i]}`,
              transform: "translateX(-50%)",
            }}
          />
        </motion.div>
      ))}

      {/* Data stream lines */}
      {[0, 1, 2].map((i) => (
        <motion.div
          key={`stream-${i}`}
          className="absolute"
          animate={{
            opacity: isHovered ? [0, 0.7, 0] : [0, 0.5, 0],
            scaleY: [0, 1, 0],
          }}
          transition={{
            duration: isHovered ? 1.5 : 3,
            repeat: Infinity,
            delay: i * (isHovered ? 0.5 : 1),
            ease: "easeInOut",
          }}
          style={{
            width: isHovered ? "3px" : "2px",
            height: isHovered ? "350px" : "300px",
            background: `linear-gradient(to bottom, transparent, ${["#7B68EE", "#60A5FA", "#A78BFA"][i]}, transparent)`,
            transform: `rotate(${i * 120}deg)`,
            transformOrigin: "center center",
            transition: "width 0.3s ease, height 0.3s ease",
          }}
        />
      ))}

      {/* Pulse rings on hover */}
      {isHovered && [0, 1, 2].map((i) => (
        <motion.div
          key={`pulse-${i}`}
          animate={{
            scale: [1, 2.5],
            opacity: [0.5, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: i * 0.6,
            ease: "easeOut",
          }}
          style={{
            position: "absolute",
            width: "100px",
            height: "100px",
            borderRadius: "50%",
            border: "2px solid rgba(123, 104, 238, 0.5)",
          }}
        />
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HERO VISUAL COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export const HeroVisual = () => {
  const [isOrbHovered, setIsOrbHovered] = useState(false);

  return (
    <div 
      className="relative w-full h-full"
      style={{ perspective: "1200px" }}
      data-testid="hero-visual-container"
    >
      {/* Canvas particles background */}
      <CanvasParticles isOrbHovered={isOrbHovered} />
      
      {/* Interactive animated orb */}
      <AdvancedOrb onHoverChange={setIsOrbHovered} />
    </div>
  );
};
